// Barcodes for labels (Stage 2.6B): EAN-13 / GTIN and Code 128 (with GS1-128 for SSCC carton labels).
// Pure functions giving bar patterns and dimensions; the label renderer draws them as SVG.

// ---- EAN-13 -------------------------------------------------------------------------------------------------------
const L = ["0001101", "0011001", "0010011", "0111101", "0100011", "0110001", "0101111", "0111011", "0110111", "0001011"];
const G = ["0100111", "0110011", "0011011", "0100001", "0011101", "0111001", "0000101", "0010001", "0001001", "0010111"];
const R = L.map((p) => p.split("").map((b) => (b === "1" ? "0" : "1")).join(""));
const PARITY = ["LLLLLL", "LLGLGG", "LLGGLG", "LLGGGL", "LGLLGG", "LGGLLG", "LGGGLL", "LGLGLG", "LGLGGL", "LGGLGL"];

/** The 95 modules of an EAN-13 ("1" = bar). The value must be 13 digits with a correct check digit. */
export function ean13Modules(ean: string): string {
  if (!/^\d{13}$/.test(ean)) throw new Error("EAN-13 needs 13 digits.");
  const d = ean.split("").map(Number);
  const parity = PARITY[d[0]];
  let out = "101";
  for (let i = 1; i <= 6; i++) out += (parity[i - 1] === "L" ? L : G)[d[i]];
  out += "01010";
  for (let i = 7; i <= 12; i++) out += R[d[i]];
  return `${out}101`;
}
/** EAN-13 at 100% (X = 0.33 mm): 37.29 × 25.93 mm including quiet zones; the minimum magnification is 80%. */
export const EAN13 = { widthMm: 37.29, heightMm: 25.93, barHeightMm: 22.85, minMagnification: 0.8, maxMagnification: 2 };

// ---- Code 128 -------------------------------------------------------------------------------------------------------
/** Bar / space widths (in modules) of each Code 128 symbol 0–106. Every symbol is 11 modules; Stop (106) is 13. */
export const CODE128 = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];
const START_B = 104, START_C = 105, CODE_B = 100, CODE_C = 99, FNC1 = 102, STOP = 106;

/**
 * Symbol values for a Code 128 barcode: code set C for runs of 4+ digits (compact), B otherwise.
 * With `gs1`, FNC1 follows the start character (GS1-128, e.g. "(00)" + SSCC).
 */
export function code128Values(text: string, gs1 = false): number[] {
  if (!text.length) throw new Error("Nothing to encode.");
  for (const ch of text) { const c = ch.charCodeAt(0); if (c < 32 || c > 126) throw new Error(`Code 128 can't encode “${ch}”.`); }
  const values: number[] = [];
  let set: "B" | "C";
  const digitRun = (i: number) => { let n = 0; while (i + n < text.length && /\d/.test(text[i + n])) n++; return n; };
  const first = digitRun(0);
  set = first >= 4 || (first === text.length && first % 2 === 0) ? "C" : "B";
  values.push(set === "C" ? START_C : START_B);
  if (gs1) values.push(FNC1);
  let i = 0;
  while (i < text.length) {
    const run = digitRun(i);
    if (set === "B" && run >= 4) {
      // Switch to C for an even number of digits.
      const even = run - (run % 2);
      values.push(CODE_C); set = "C";
      for (let k = 0; k < even; k += 2) values.push(Number(text.slice(i + k, i + k + 2)));
      i += even;
      continue;
    }
    if (set === "C") {
      if (run >= 2) { values.push(Number(text.slice(i, i + 2))); i += 2; continue; }
      values.push(CODE_B); set = "B";
    }
    values.push(text.charCodeAt(i) - 32);
    i++;
  }
  const check = values.reduce((sum, v, k) => sum + v * (k === 0 ? 1 : k), 0) % 103;
  values.push(check, STOP);
  return values;
}
/** Bar and space widths in modules, starting with a bar. */
export function code128Widths(text: string, gs1 = false): number[] {
  return code128Values(text, gs1).flatMap((v) => CODE128[v].split("").map(Number));
}
/** Code 128 needs a quiet zone of 10 modules each side; modules below 0.25 mm are hard to print and scan reliably. */
export const CODE128_RULES = { quietModules: 10, minModuleMm: 0.25, minHeightMm: 6 };

// ---- GS1 SSCC (carton labels) ----------------------------------------------------------------------------------------------
/** GS1 mod-10 check digit for a string of digits (SSCC, GTIN). */
export function gs1CheckDigit(digits: string): number {
  const sum = digits.split("").reverse().reduce((t, d, i) => t + Number(d) * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10;
}
/** An 18-digit SSCC: extension digit + GS1 company prefix + serial reference, padded to 17 digits, + check digit. */
export function makeSscc(extension: number, companyPrefix: string, serial: number): string {
  const body = `${extension}${companyPrefix}`;
  const serialLen = 17 - body.length;
  if (!/^\d+$/.test(companyPrefix) || serialLen < 1) throw new Error("The GS1 company prefix must be 6–12 digits.");
  const s17 = `${body}${String(serial).padStart(serialLen, "0").slice(-serialLen)}`;
  return `${s17}${gs1CheckDigit(s17)}`;
}
